import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    ContentLayout,
    FormField,
    Header,
    Input,
    SpaceBetween,
    Spinner,
    Table,
} from '@cloudscape-design/components';
import apiAxios from '../../client/apiClient';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface Instrument {
    ticker?: string;
    name?: string;
    shortName?: string;
    type?: string;
    currencyCode?: string;
    isin?: string;
    extendedHours?: boolean;
    workingScheduleId?: number;
    maxOpenQuantity?: number;
    minTradeQuantity?: number;
    addedOn?: string;
}

export const Trading212AdminPage: React.FC = () => {
    useDocumentTitle('Trading 212 — Admin');

    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<Instrument[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    const lookup = async () => {
        if (!query.trim()) return;
        setError(null);
        setLoading(true);
        setResults(null);
        try {
            const resp = await apiAxios.get<Instrument[]>(
                `${API_BASE}/api/trading212-account/instruments`,
                { params: { query: query.trim(), limit: 20 } },
            );
            setResults(resp.data);
        } catch (err: any) {
            const msg = err.response?.data?.error || err.message || 'Unknown error';
            setError(`Lookup failed: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ContentLayout header={<Header variant="h1">Trading 212</Header>}>
            <SpaceBetween size="l">
                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Look up a ticker's Trading 212 metadata — currency, max open quantity, working schedule, extended-hours flag, and ISIN. Enter a symbol (e.g. MXL) or full Trading 212 ticker (MXL_US_EQ)."
                        >
                            Instrument Lookup
                        </Header>
                    }
                >
                    <SpaceBetween size="m">
                        {error && (
                            <Alert type="error" dismissible onDismiss={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                        <FormField label="Ticker">
                            <Input
                                value={query}
                                onChange={({ detail }) => setQuery(detail.value)}
                                onKeyDown={({ detail }) => {
                                    if (detail.key === 'Enter') lookup();
                                }}
                                placeholder="MXL or MXL_US_EQ"
                            />
                        </FormField>
                        <Box>
                            <Button
                                variant="primary"
                                loading={loading}
                                disabled={loading || !query.trim()}
                                onClick={lookup}
                            >
                                Look up
                            </Button>
                        </Box>

                        {loading && <Spinner />}

                        {results !== null && results.length === 0 && (
                            <Alert type="info">No instruments matched "{query}".</Alert>
                        )}

                        {results !== null && results.length > 0 && (
                            <Table<Instrument>
                                items={results}
                                columnDefinitions={[
                                    { id: 'ticker', header: 'Ticker', cell: i => i.ticker ?? '' },
                                    { id: 'shortName', header: 'Symbol', cell: i => i.shortName ?? '' },
                                    { id: 'name', header: 'Name', cell: i => i.name ?? '' },
                                    { id: 'type', header: 'Type', cell: i => i.type ?? '' },
                                    { id: 'currency', header: 'Currency', cell: i => i.currencyCode ?? '' },
                                    {
                                        id: 'maxOpen',
                                        header: 'Max open qty',
                                        cell: i => i.maxOpenQuantity != null ? Number(i.maxOpenQuantity).toLocaleString() : '—',
                                    },
                                    {
                                        id: 'minTrade',
                                        header: 'Min trade qty',
                                        cell: i => i.minTradeQuantity != null ? Number(i.minTradeQuantity).toLocaleString() : '—',
                                    },
                                    { id: 'extHours', header: 'Extended hours', cell: i => i.extendedHours ? 'Yes' : 'No' },
                                    { id: 'workingScheduleId', header: 'Schedule ID', cell: i => i.workingScheduleId ?? '—' },
                                    { id: 'isin', header: 'ISIN', cell: i => i.isin ?? '' },
                                    { id: 'addedOn', header: 'Added', cell: i => (i.addedOn ?? '').substring(0, 10) },
                                ]}
                                variant="container"
                                stickyHeader
                            />
                        )}
                    </SpaceBetween>
                </Container>
            </SpaceBetween>
        </ContentLayout>
    );
};
